import React, { Component } from "react";
import { Helmet } from "react-helmet-async";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { stormSrc } from "core-web/libs/image";
import { injectModels } from "core-web/state";
import decodeHTMLEntities from "core-web/utils/decodeHTMLEntities";
import getCountrySpecificVariable from "core-web/utils/getCountrySpecificVariable";
import { getMediaUrl } from "core-web/utils/getMediaUrl";
import { getProductAvailability } from "core-web/utils/getProductAvailability";
import { getHreflangs, getLanguages } from "core-web/utils/multimarket";
import { getParametric } from "core-web/utils/storm";
import { toFixedCurrency } from "core-web/components/FormatPrice";
import LanguageLinks from "core-web/components/Meta/LanguageLinks";
import ProductStructuredData from "core-web/components/Meta/StructuredData/ProductStructuredData";

class ProductMeta extends Component {
    static propTypes = {
        application: PropTypes.object.isRequired,
        page: PropTypes.object.isRequired,
        product: PropTypes.object.isRequired,
        t: PropTypes.func.isRequired,
    };

    shouldComponentUpdate(nextProps) {
        if (JSON.stringify(nextProps.product) !== JSON.stringify(this.props.product)) {
            return true;
        }

        return false;
    }

    subDesc = (string) => {
        return string.length >= 160
            ? string
                  .replace(/<\/{0,1}[^>]+>/g, "")
                  .slice(0, 157)
                  .concat("...")
            : string.replace(/<\/{0,1}[^>]+>/g, "");
    };

    getImagesFromFiles = (files) => {
        return files.reduce((acc, cur) => {
            const { format } = cur;

            if (format.image) {
                return [...acc, getMediaUrl(format.image)];
            }

            return acc;
        }, []);
    };

    getValidMetaDescription = (description) => {
        let strippedDescription = description;
        for (let i = 0; i < 3; i++) {
            if (strippedDescription.length > 155) {
                strippedDescription = strippedDescription.split("✓").slice(0, -1).join("✓");
            }
        }

        return strippedDescription;
    };

    render() {
        const { application, product, page, t } = this.props;

        if (!product) {
            return null;
        }

        const protocol = application?.protocol || "";
        const origin = application?.origin || "";
        const baseUrl = protocol + origin;
        const { ecommerceEnabled } = application;

        const currency = application?.currency?.code || null;
        const imageSrc = (product.format || {}).image || (product.files[0] && product.files[0].format.image);
        const mpn = product.manufacturer?.part_no || null;
        const sku = product.part_no;
        const ean = product.ean_code;
        const brand = product.manufacturer?.name || product.manufacturer?.unique_name;
        let price = product.price * product.vat_rate;
        if (currency === "EUR") {
            price = toFixedCurrency(price, 2, ".");
        } else if (price % 1 !== 0) {
            price = toFixedCurrency(price, 2, ".");
        }

        const includedProducts = product.included_products;

        const images = this.getImagesFromFiles(product.files);
        imageSrc && images.unshift(stormSrc(imageSrc, { m: "pad", f: "webp" }, true));

        const seoProductTitle = getParametric("title_seo", product)?.value;
        const titleFallback = t("seo.product_title_fallback", { product: product.name });

        const productMetaDescription = getParametric("meta_description", product)?.value;
        const metaDescriptionFallback = this.getValidMetaDescription(
            t("seo.product_description_fallback", { product: product.name }),
        );

        const title = seoProductTitle || titleFallback;
        const productDescription = product.description && product.description !== "" ? product.description : null;
        const metaDescription = productMetaDescription || productDescription || metaDescriptionFallback;

        const productUrl = `${baseUrl}${product.url}`;

        const keywords = product.keywords || null;

        const productAvailability = getProductAvailability(product, application?.locale);

        const ogType = "product";
        const ogTitle = title;
        const ogDescription = metaDescription || null;
        const ogImage = imageSrc;
        const ogUrl = productUrl || null;
        const ogLocale = application?.locale || null;
        const ogProductPrice = typeof price === "number" ? toFixedCurrency(price, 2, ".") : price;
        const ogProductCurrency = currency;
        const ogProductAvailability = productAvailability.inStock
            ? "in stock"
            : productAvailability.preOrder || productAvailability.backOrder
              ? "available for order"
              : "out of stock";
        const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
        const facebookDomainVerification = getCountrySpecificVariable(
            "REACT_APP_FACEBOOK_DOMAIN_VERIFICATION",
            ogLocale,
        );

        const localeAlternates = page.localeAlternates || null;
        let hreflangs;

        if (localeAlternates) {
            const languages = getLanguages(application.languages, ogLocale);
            hreflangs = getHreflangs(localeAlternates, languages);
        }

        return (
            <>
                <Helmet>
                    {title && <title>{decodeHTMLEntities(title)}</title>}
                    {productUrl && <link href={productUrl} rel="canonical" />}
                    {metaDescription && (
                        <meta name="description" content={this.subDesc(decodeHTMLEntities(metaDescription))} />
                    )}
                    {keywords && <meta name="keywords" content={keywords} />}

                    {ogType && <meta property="og:type" content={ogType} />}
                    {ogTitle && <meta property="og:title" content={ogTitle} />}
                    {ogDescription && <meta property="og:description" content={this.subDesc(ogDescription)} />}
                    {ogImage && <meta property="og:image" content={ogImage} />}
                    {ogUrl && <meta property="og:url" content={ogUrl} />}
                    {ogLocale && <meta property="og:locale" content={ogLocale} />}
                    {ecommerceEnabled && ogProductPrice && <meta property="og:price:amount" content={ogProductPrice} />}
                    {ecommerceEnabled && ogProductCurrency && (
                        <meta property="og:price:currency" content={ogProductCurrency} />
                    )}
                    {brand && <meta property="product:brand" content={brand} />}
                    {ogProductAvailability && <meta property="product:availability" content={ogProductAvailability} />}
                    <meta property="product:condition" content="new" />
                    {ogLocale && <meta property="product:locale" content={ogLocale} />}
                    {ecommerceEnabled && ogProductPrice && (
                        <meta property="product:price:amount" content={ogProductPrice} />
                    )}
                    {ecommerceEnabled && ogProductCurrency && (
                        <meta property="product:price:currency" content={ogProductCurrency} />
                    )}
                    {facebookAppId && <meta property="fb:app_id" content={facebookAppId} />}
                    {facebookDomainVerification && (
                        <meta name="facebook-domain-verification" content={facebookDomainVerification} />
                    )}
                </Helmet>
                <ProductStructuredData
                    brand={brand}
                    categories={product.categories}
                    currency={currency}
                    description={metaDescription}
                    ean={ean}
                    images={images}
                    includedProducts={includedProducts}
                    productAvailability={productAvailability}
                    mpn={mpn}
                    name={product.name}
                    price={price}
                    sku={sku}
                    url={productUrl}
                    variants={product.variants || []}
                />
                {hreflangs && <LanguageLinks links={hreflangs} />}
            </>
        );
    }
}

export default injectModels(["application", "page"])(withTranslation()(ProductMeta));
